import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Badge, Tooltip } from 'antd'
import {
  CheckCircleFilled,
  CloseCircleFilled,
  InfoCircleOutlined,
  UserOutlined, 
  TeamOutlined
} from '@ant-design/icons'
import { ArrowRight } from 'react-bootstrap-icons'
import Footer from '../Footer'
import PublicHeader from '../Header/PublicHeader'
import banner from "../../Assets/img/banner2.avif";
import { TESTIMONIALS } from '../../Assets/data/common';


const MembershipCard = ({ title, price, subtitle, features, link }) => (
  <div className="bg-white  shadow-lg mb-4 "style={{borderRadius:"20px"}}>
    <div className=" text-white p-4 rounded-t-lg" style={{background:"#244183"}}>
      <h3 className="font-bold text-xl text-center">{title}</h3>
      <div className="text-3xl font-bold mt-2 text-center">₹{price}</div>
      {subtitle && <div className="text-sm mt-1 text-center opacity-90">{subtitle}</div>}
    </div>

    <div className="p-4">
      {features.map((section, sIndex) => (
        <div key={sIndex} className="mb-6">
          <div className="bg-gray-50 -mx-4 px-4 py-2 sticky top-0 z-10">
            <h4 className="font-bold text-[#0d73b9] text-lg">{section.category}</h4>
          </div>
          <div className="mt-3">
            {section.items.map((item, iIndex) => (
              <div key={iIndex} className="flex items-start py-2 border-b border-gray-100 last:border-0">
                <div className="mt-1 mr-3">
                  {item.included[title === "Individual Membership - 1 Year " ? 0 : title === "Life Membership - 10 Years " ? 1 : 2] ? (
                    <CheckCircleFilled className="text-green-500 text-lg" />
                  ) : (
                    <CloseCircleFilled className="text-red-500 text-lg" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-start gap-2">
                    <span className="text-sm font-medium">{item.name}</span>
                    {item.note && (
                      <Tooltip title={item.note}>
                        <InfoCircleOutlined className="text-gray-400 mt-0.5 flex-shrink-0" />
                      </Tooltip>
                    )}
                  </div>
                  {item.subItems && (
                    <ul className="list-disc ml-4 mt-1 text-xs text-gray-600">
                      {item.subItems.map((subItem, subIndex) => (
                        <li key={subIndex}>{subItem}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>

    <div className="p-4 bg-gray-50 rounded-b-lg">
      <a
        className=" text-white px-6 py-3 rounded-lg text-base font-semibold 
        hover:bg-[#0a5a94] transition-colors duration-300 
        shadow-lg hover:shadow-xl
        flex items-center justify-center gap-2 w-full"
        style={{background:"linear-gradient(180deg, #0078BF 0%, #244183 100%)", borderRadius:"20px"}}
        href={link}
      >
        <span>{title === "Individual Membership - 1 Year " ? "Get Individual Membership" :
          title === "Life Membership - 10 Years " ? "Get Life Membership" :
            "Get Organisational Membership"}</span>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </a>
    </div>
  </div>
);

const MembershipTable = () => {
  const [showHighlight, setShowHighlight] = useState(false)
  const navigate = useNavigate()
  const [showPopup, setShowPopup] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0);
  const [expandedQuotes, setExpandedQuotes] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);


  const togglePopup = () => setShowPopup(!showPopup)
  const features = [
    {
      category: 'CREDIBILITY',
      items: [
        {
          name: "Listing on Association's Website",
          included: [true, true, true]
        },
        { name: 'Membership Badge', 
          included: [true, true, true] 
        },
        {
          name: 'Publish Articles in APCCI Newsletter',
          included: [true, true, true]
        },
        {
          name: 'Eligible for APCCI Rewards & Recognition',
          included: [true, true, true]
        },
        { name: 'Certificate of Membership', 
          included: [false, false, true] 
        }
      ]
    },
    {
      category: 'TRAINING & EMPOWERMENT',
      items: [
        {
          name: 'APCCI Networking Events & Programs',
          subItems: ['Conferences', 'Get-Togethers', 'Destination Events'],
          note: 'Exclusive Discount on Registration charges',
          included: [true, true, true]
        },
        {
          name: 'Certificate Programs by APCCI',
          note: 'Exclusive Discount on the Price',
          included: [true, true, true]
        },
        {
          name: 'Career Counsellor Training Modules',
          included: [true, true, true]
        },
        {
          name: 'Priority Invitations for APCCI Networking Events',
          included: [true, true, true]
        },
        { name: 'Career Awareness Webinars', 
          included: [true, true, true] 
        },
        {
          name: 'APCCI Exclusive Paid Webinars',
          note: '4 per year FREE (Exclusive Discount on 5th onwards)',
          included: [true, true, true]
        }
      ]
    },
    {
      category: 'APCCI EXCLUSIVE RESOURCES',
      items: [
        {
          name: 'Access to APCCI Exclusive Member Dashboard',
          included: [true, true, true]
        },
        {
          name: 'Counselling Business Starting Kit (Virtual)',
          included: [true, true, true]
        },
        {
          name: 'APCCI Previous Webinar Recordings',
          included: [true, true, true]
        },
        {
          name: 'Access to APCCI Research on Career Trends, Demand and on Tools & Techniques',
          included: [true, true, true]
        },
        { name: 'Newsletters & Publications', 
          included: [true, true, true] 
        }
      ]
    },
    {
      category: 'GROWTH & LEARNING OPPORTUNITIES',
      items: [
        {
          name: 'Eligible to Serve in APCCI Functional Committees',
          included: [true, true, true]
        },
        {
          name: 'Eligible for Leadership Roles in APCCI',
          included: [false, true, true]
        },
        {
          name: 'Eligible to be a part of APCCI Centre of Excellence',
          included: [false, true, true]
        }
      ]
    }
  
  ]

  const membershipTypes = [
    {
      title: "Individual Membership - 1 Year ",
      price: " 4,800 ",
      subtitle: (
        <>
          <span style={{ fontWeight: 'bold '}}>(Annual Membership)<br /></span>
          <span style={{ fontStyle: 'italic' }}>To be renewed by 31st March 2027</span>
        </>
      ),
      link: "https://billing.apcci.in/subscribe/1c894a1a4516c02224251419503f99a987f710be23c65b6adbde1ccf1c7a189d/Annual?addon_code%5B0%5D=dashboard&addon_quantity%5B0%5D=1&addon_code%5B1%5D=Addonannual&addon_quantity%5B1%5D=1"
    },
    {
      title: "Life Membership - 10 Years ",
      price: " 19,200 ",
      subtitle: (
        <>
          <span style={{ fontWeight: 'bold' }}>(4+6 years FREE Membership)<br/></span>
          <span style={{ fontStyle: 'italic' }}>To be renewed by 31st March 2035</span>
        </>
      ),
      link: "https://billing.apcci.in/subscribe/1c894a1a4516c02224251419503f99a987f710be23c65b6adbde1ccf1c7a189d/Life10years?addon_code%5B0%5D=lifeaddon&addon_quantity%5B0%5D=1"
    },
    {
      title: "Organisational Membership - 1 Year ",
      price: " 9,600 ",
      subtitle: (
        <>
          <span style={{ fontWeight: 'bold' }}>(Registered Entities)<br /></span>
          <span style={{ fontStyle: 'italic' }}>To be renewed by 31st March 2027</span>
        </>
      ),
      link: "https://billing.apcci.in/subscribe/1c894a1a4516c02224251419503f99a987f710be23c65b6adbde1ccf1c7a189d/Org?addon_code%5B0%5D=Orgaddon&addon_quantity%5B0%5D=1&addon_code%5B1%5D=dashbardorg&addon_quantity%5B1%5D=1"
    }
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowHighlight(true); // Show the element after 6 seconds
    }, 6000);
    return () => clearTimeout(timer); // Cleanup the timer
  }, []);

  useEffect(() => {
    const carouselElement = document.getElementById('testimonialCarousel');
    if (carouselElement) {
      carouselElement.addEventListener('slide.bs.carousel', event => {
        setActiveIndex(event.to);
      });
    }
  }, []);
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);

    return () => clearInterval(interval);
  }, [currentIndex]);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex + 1 >= TESTIMONIALS.length ? 0 : prevIndex + 1
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex - 1 < 0 ? TESTIMONIALS.length - 1 : prevIndex - 1
    );
  };

  const toggleQuote = index => {
    setExpandedQuotes(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };
  const [cardsToShow, setCardsToShow] = useState(window.innerWidth <= 768 ? 1 : 3);

  useEffect(() => {
    const handleResize = () => {
      setCardsToShow(window.innerWidth <= 768 ? 1 : 3);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getVisibleTestimonials = () => {
    const result = [];
    for (let i = 0; i < cardsToShow; i++) {
      const index = (currentIndex + i) % TESTIMONIALS.length;
      result.push({ ...TESTIMONIALS[index], originalIndex: index });
    }
    return result;
  };

  const visibleTestimonials = getVisibleTestimonials();


  return (
    <>
      <PublicHeader />
      <div
        className="section-header"
         
      >
        <div className="header-content px-5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-left"
          >
            <h1 className="text-center display-4 fw-bold mb-2 text-white">Membership Plans</h1>

          </motion.div>
        </div>
        <div className="overlay"></div>
      </div>
      <div className='max-w-8xl sm:mx-2 lg:mx-10 md:mx-10 px-2 sm:px-4 py-4 sm:py-8 justify-center'>
        <div className="w-full max-w-8xl mx-auto px-4 sm:px-6 py-6 overflow-x-hidden">

          {/* Main Heading */}
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-[#0d73b9] mb-6">
            Eligibility Criteria
          </h2>

          {/* Individual/Life Member Section */}
          <div className="w-full bg-[#E7F4FD] p-6 rounded-xl shadow-md mb-6">
            <h3 className="text-lg sm:text-xl font-semibold text-[#0d73b9] mb-4 text-center flex flex-wrap justify-center items-center gap-2">
              <UserOutlined className="text-base" />
              <span>To Become Individual/Life Member</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-gray-700 break-words">
              <div className="flex items-start">
                <CheckCircleFilled className="text-green-500 text-base mr-2 mt-1" />
                <span className="break-words">Minimum required qualification: Graduation in any discipline</span>
              </div>
              <div className="flex items-start">
                <CheckCircleFilled className="text-green-500 text-base mr-2 mt-1" />
                <span className="break-words">Career counselling certificate of minimum 1-month duration</span>
              </div>
              <div className="flex items-start">
                <CheckCircleFilled className="text-green-500 text-base mr-2 mt-1" />
                <span className="break-words">Certificate, diploma, or degree in psychology domain (optional)</span>
              </div>
              <div className="flex items-start">
                <CheckCircleFilled className="text-green-500 text-base mr-2 mt-1" />
                <span className="break-words">Applicant should be an Indian citizen</span>
              </div>
            </div>
          </div>

          {/* Organisational Member Section */}
          <div className="w-full bg-[#E7F4FD] p-6 rounded-xl shadow-md">
            <h3 className="text-lg sm:text-xl font-semibold text-[#0d73b9] mb-4 text-center flex flex-wrap justify-center items-center gap-2">
              <TeamOutlined className="text-base" />
              <span>To Become Organisational Member</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-gray-700 break-words">
              <div className="flex items-start">
                <CheckCircleFilled className="text-green-500 text-base mr-2 mt-1" />
                <span className="break-words">Registered Entities (Companies/Firms/Educational Institutions)</span>
              </div>
              <div className="flex items-start">
                <CheckCircleFilled className="text-green-500 text-base mr-2 mt-1" />
                <span className="break-words">Actively provides Career Guidance services</span>
              </div>
              <div className="flex items-start">
                <CheckCircleFilled className="text-green-500 text-base mr-2 mt-1" />
                <span className="break-words">Team of Certified Career Counsellors</span>
              </div>
              <div className="flex items-start">
                <CheckCircleFilled className="text-green-500 text-base mr-2 mt-1" />
                <span className="break-words">Entities should be registered in India</span>
              </div>
            </div>
          </div>
        </div>
        {/* {showHighlight && (
          <motion.div
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 100, opacity: 0 }}
            className="fixed bottom-8 right-8 sm:fixed bottom-2 right-4 flex items-end justify-center z-50 p-3"
          >
            <div className="relative rounded-lg shadow-2xl p-6 max-w-sm w-full border-l-4 border-[#0d73b9]"
            style={{
            background: "linear-gradient(180deg, #0078BF 0%, #244183 100%)"
            }}
            >
            <button
              onClick={() => setShowHighlight(false)}
              className="absolute top-2 right-2 text-white hover:text-gray-600"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="flex items-center space-x-4">
                <div className="bg-white rounded-full p-3">
                <svg className="w-6 h-6 text-[#0d73b9]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                </div>
                <div>
                  <h3 className="font-bold text-white">Limited-Time Offer!</h3>
                  <p className="text-sm text-white">Get APCCI Membership at Pre-Launch Discount</p>
                  <div>
                  <button
                    onClick={togglePopup}
                    className="mt-2 text-sm font-semibold text-white hover:text-gray-700 flex items-center"
                  >
                    Register Now<ArrowRight className="ml-1" />
                  </button>
                  </div>
                </div>
            </div>
            </div>
          </motion.div>
        )} */}
        <div
          className="mx-4 md:mx-20 my-6 rounded-xl shadow-xl text-white py-6 px-6 flex flex-col md:flex-row md:items-stretch md:justify-between text-center md:text-left"
          style={{
          background: "linear-gradient(90deg, #0078BF 0%, #244183 100%)",
          boxShadow: "inset 0 1px 3px rgba(255,255,255,0.2)"
          }}
        >
          <div className="w-full flex flex-col justify-center">
            <h3 className="font-bold text-base md:text-xl leading-snug md:leading-normal mb-2 tracking-wide">
              Your chance to join at the best rate! Avail Exclusive Discount at Payments Page 🎁
            </h3>
            {/* <p className="text-sm md:text-base leading-snug md:leading-relaxed opacity-90">
              Submit your details to receive an exclusive coupon code directly on your email.
            </p> */}
          </div>

          {/* <div className="w-full md:w-1/4 flex justify-center items-center mt-4 md:mt-0 md:mb-4">
            <button
              onClick={togglePopup}
              className="bg-white bg-opacity-80 hover:bg-gradient-to-r hover:from-white hover:to-gray-100 text-[#15629e] font-semibold py-2 px-6 rounded-full text-sm md:text-base transition-all duration-300 shadow-md hover:shadow-lg mb-2 transform hover:scale-110 border border-[#15629e]"
            >
              Get Coupon
            </button>
          </div> */}
        </div>

        <div className='text-end mb-4 sm:mb-6'>
          <Badge
            color='red'
            text='Price below is Excluding GST (if applicable)'
            className='text-sm sm:text-lg'
          />
        </div>

        {/* Mobile View (Cards) */}
        <div className="md:hidden space-y-6">
          {membershipTypes.map((type, index) => (
            <MembershipCard
              key={index}
              title={type.title}
              price={type.price}
              subtitle={type.subtitle}
              features={features}
              link={type.link}
            />
          ))}
        </div>

        {/* Desktop View (Cards in a Row) */}
        <div className="hidden md:flex justify-center gap-6">
          {membershipTypes.map((type, index) => (
            <MembershipCard
              key={index}
              title={type.title}
              price={type.price}
              subtitle={type.subtitle}
              features={features}
              link={type.link}
            />
          ))}
        </div>

        {/* Pop-up */}
        <AnimatePresence>
          {showPopup && (
            <motion.div
              className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center pt-20 z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="bg-white rounded-lg shadow-lg p-6 w-[90%] md:w-[600px] relative"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
              >
                <button
                  className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                  onClick={togglePopup}
                >
                  ✕
                </button>

                <div className="w-full h-[420px] items-center justify-center lg:h-[450px] lg:mt-[45px]">
                  <iframe
                    src="https://pay.apcci.in/widget/form/YPeHnE4CyiixKL1a81eG"
                    style={{ width: '100%', height: '100%', border: 'none', borderRadius: '4px' }}
                    title="Register for Pre-Launch"
                  />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <section className='py-20 bg-[#F5F5F5] ' style={{ borderRadius: "18px" }}>
          <div className='max-w-8xl  lg-px-4 sm-mx-0 sm-px-0  md-px-4 container  md:mx-12 mx-auto '>
            <h2 className='text-4xl font-bold text-center mb-20'>
              <span className="text-black">What Makes Us Proud:</span> <span className="text-[#0078BF]">Our Members' Journeys</span>
            </h2>


            {/* Testimonial Cards */}
            <div className="relative">
              <div className="flex justify-center">
                {visibleTestimonials.map((testimonial, idx) => (
                  <div key={idx} className="w-full md:w-2/3 px-4  relative">
                    <div className="bg-white  shadow-md overflow-hidden " style={{borderRadius:"20px"}}>
                      {/* Image */}
                      <div className="w-full flex justify-center pt-4 top-[-75px] absolute left-0 ">
                        <div className="w-24 h-24 rounded-full overflow-hidden">
                          <img
                            src={testimonial.image}
                            alt={testimonial.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>

                      {/* Details */}
                      <div className="p-6 mt-10 ">
                        <h3 className="text-xl font-bold text-center">{testimonial.name}</h3>
                        <p className="text-gray-600 text-center">{testimonial.role}</p>
                        <p className="text-gray-600 text-center">{testimonial.organization}</p>

                        {/* Testimonial Text */}
                        <div className="mt-4 border-t pt-4 ">
                          <p className="text-gray-600 text-justify overflow-y-scroll h-[150px]" style={{ textWrap: 'pretty' }}>
                            {testimonial.quote}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Navigation Buttons */}
              <div className=" left-0 right-0 bottom-0 flex justify-center mt-4 space-x-4 gap-20">
                <button
                  onClick={nextSlide}
                  className="w-12 h-12 flex items-center justify-center rounded-full bg-white shadow-md border border-gray-200 text-blue-500 hover:bg-gray-50 transition-colors duration-200"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={prevSlide}
                  className="w-12 h-12 flex items-center justify-center rounded-full bg-white shadow-md border border-gray-200 text-blue-500 hover:bg-gray-50 transition-colors duration-200"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Call to action button */}
            
          </div>

        </section>
      <Footer />
    </>
  )
}

export default MembershipTable
